import { useState } from 'react';
import clsx from 'clsx';
import { cancelAppointment, OrderConflictError } from '../../lib/orders';
import { formatDateTime } from '../../lib/format';

interface CancelAppointmentDialogProps {
  orderId: string;
  ritId: string;
  fase: 'pickup' | 'delivery';
  datum: string | null;
  /** True wanneer rit-status !== 'Ingepland' (bv. 'Verwerkt'). Toont waarschuwing + verstuurt enforceStatusGuard=false. Kantoor-only (admin/medewerker). */
  recoveryMode?: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const FASE_LABEL: Record<'pickup' | 'delivery', string> = {
  pickup: 'Ophaalafspraak',
  delivery: 'Afleverafspraak',
};

const MIN_REDEN_LENGTH = 5;

export function CancelAppointmentDialog({
  orderId,
  ritId,
  fase,
  datum,
  recoveryMode = false,
  onClose,
  onSaved,
}: CancelAppointmentDialogProps) {
  const [reden, setReden] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = reden.trim();
  const canSubmit = !submitting && trimmed.length >= MIN_REDEN_LENGTH;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await cancelAppointment({
        orderId,
        ritId,
        fase,
        reden: trimmed,
        enforceStatusGuard: !recoveryMode,
      });
      onSaved();
    } catch (err) {
      if (err instanceof OrderConflictError) {
        setError('Deze afspraak is ondertussen gewijzigd. Ververs de pagina en probeer opnieuw.');
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Afspraak annuleren mislukt');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">
            {recoveryMode ? `${FASE_LABEL[fase]} forceer-annuleren` : `${FASE_LABEL[fase]} annuleren`}
          </h2>
          {datum && (
            <p className="text-xs text-slate-500 mt-1">
              Afspraak op {formatDateTime(datum)}
            </p>
          )}
        </div>

        <div className="px-6 py-4 space-y-3">
          {recoveryMode && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              <p className="font-semibold mb-1">Recovery-pad</p>
              <p>
                Deze rit staat al op <code>Verwerkt</code> — de route is gefinaliseerd.
                Annuleren ontkoppelt 'm zodat de WO opnieuw planbaar wordt.
                Gebruik dit alleen voor herstel na een foutieve route-finalize of
                een klacht die de WO heropent. De WO-status wordt niet automatisch
                teruggerold — die zet je daarna zelf.
              </p>
            </div>
          )}

          <p className="text-sm text-slate-700">
            De order blijft openstaan en kan opnieuw ingepland worden. Alleen
            deze afspraak wordt geannuleerd — niet de hele order.
          </p>

          <label className="block">
            <span className="text-sm text-slate-700">Reden</span>
            <textarea
              value={reden}
              onChange={(e) => setReden(e.target.value)}
              rows={3}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              placeholder="Bv. klant wil nabehandeling"
              disabled={submitting}
              autoFocus
            />
            {trimmed.length > 0 && trimmed.length < MIN_REDEN_LENGTH && (
              <p className="text-xs text-amber-600 mt-1">
                Minimaal {MIN_REDEN_LENGTH} tekens.
              </p>
            )}
          </label>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              !canSubmit
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
            )}
          >
            {submitting
              ? 'Annuleren…'
              : recoveryMode
              ? 'Forceer annuleren'
              : 'Afspraak annuleren'}
          </button>
        </div>
      </div>
    </div>
  );
}
